// Save a card from a link (/card/<token>) and the page Stripe comes back to.
import { type PublicCardPage, saveCardSentence } from "@steward/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client";
import { CardBox, CardTermsLink, useCardFields } from "../../components/CardFields";
import { Banner, Button, Skeleton } from "../../components/parts";
import { errText } from "../../components/work";
import { Branded, NotFound } from "./common";

/** After the card link: Stripe sends the client here. The webhook saves the card. */
export function CardSavedPage({ saved }: { saved: boolean }) {
  return (
    <Branded brand={null}>
      <h1>{saved ? "Your card is saved" : "Nothing was saved"}</h1>
      <p>{saved ? "Thank you. Nothing was charged. You can close this page." : "You can use the link again any time."}</p>
    </Branded>
  );
}

// Save a card page -----------------------------------------------------------------------------------------------------

/**
 * Our own save a card page (owner, Oct 31): the office's Text them a link to save a card opens this, never Stripe's page.
 * Stripe's card fields sit in it like on the quote page (api billing/card-page.ts).
 */
export function SaveCardPage({ token }: { token: string }) {
  const page = useQuery({
    queryKey: ["public-card", token],
    queryFn: () => api<PublicCardPage>("GET", `public/card/${token}`),
    retry: false,
  });
  const [saved, setSaved] = useState<PublicCardPage | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const card = useCardFields<PublicCardPage>(token, !!page.data && !saved, "public/card");
  if (page.error) return <NotFound what="card link" />;
  const p = saved ?? page.data;
  if (!p)
    return (
      <Branded brand={null}>
        <Skeleton rows={4} />
      </Branded>
    );
  if (saved?.card)
    return (
      <Branded brand={p.brand}>
        <h1>Your card is saved</h1>
        <p>
          Thank you, {p.firstName}. {saved.card.brand} ending in {saved.card.last4} is on file with {p.brand.name}. Nothing was
          charged. You can close this page.
        </p>
      </Branded>
    );
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      setSaved(await card.save());
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e, "The card was not saved."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Branded brand={p.brand}>
      <h1>Save a card, {p.firstName}</h1>
      <p className="muted">
        {p.card
          ? `You have ${p.card.brand} ending in ${p.card.last4} on file. A new card here takes its place.`
          : `Keep a card on file with ${p.brand.name} so paying is easy.`}{" "}
        Nothing is charged now.
      </p>
      <CardBox
        card={card}
        title="Your card"
        sentence={
          <>
            {saveCardSentence(p.brand.name)} <CardTermsLink />
          </>
        }
      />
      {err ? <Banner type="error">{err}</Banner> : null}
      <Button size="large" fullWidth loading={busy} disabled={card.state === "loading"} onClick={() => void save()}>
        Save card
      </Button>
    </Branded>
  );
}

// Quote page -----------------------------------------------------------------------------------------------------------
